const protectionProperty = '_volltti_shipping_protection';

function sameVariant(left, right) {
  return String(left) === String(right);
}

export function planProtectionMutation(cart, { enabled, protectionVariantId }) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const protectionLines = items.filter((item) => sameVariant(item.variant_id, protectionVariantId));
  const merchandiseLines = items.filter(
    (item) => !sameVariant(item.variant_id, protectionVariantId) && Number(item.quantity) > 0,
  );

  if (!enabled || merchandiseLines.length === 0) {
    if (protectionLines.length === 0) return { kind: 'none' };
    return {
      kind: 'update',
      updates: Object.fromEntries(protectionLines.map((item) => [item.key, 0])),
    };
  }

  if (protectionLines.length === 0) {
    return {
      kind: 'add',
      item: {
        id: protectionVariantId,
        quantity: 1,
        properties: { [protectionProperty]: 'true' },
      },
    };
  }

  if (protectionLines.length === 1 && Number(protectionLines[0].quantity) === 1) {
    return { kind: 'none' };
  }

  return {
    kind: 'update',
    updates: Object.fromEntries(
      protectionLines.map((item, index) => [item.key, index === 0 ? 1 : 0]),
    ),
  };
}

async function readJson(response) {
  const body = await response.json();
  if (!response.ok) {
    throw new Error(body?.description || body?.message || 'Unable to update shipping protection.');
  }
  return body;
}

export async function applyProtectionChoice({
  enabled,
  protectionVariantId,
  fetchImpl = fetch,
  routes,
  sections,
  sectionsUrl,
}) {
  const cart = await readJson(await fetchImpl(routes.cart, { headers: { Accept: 'application/json' } }));
  const plan = planProtectionMutation(cart, { enabled, protectionVariantId });

  if (plan.kind === 'none') return { plan, response: null };
  const isAdd = plan.kind === 'add';
  const response = await readJson(await fetchImpl(isAdd ? routes.add : routes.update, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...(isAdd ? { items: [plan.item] } : { updates: plan.updates }),
      sections,
      sections_url: sectionsUrl,
    }),
  }));

  return { plan, response };
}

export async function reconcileExistingProtection({
  protectionVariantId,
  fetchImpl = fetch,
  routes,
  sections,
  sectionsUrl,
}) {
  const cart = await readJson(await fetchImpl(routes.cart, { headers: { Accept: 'application/json' } }));
  const hasProtection = cart.items.some((item) => sameVariant(item.variant_id, protectionVariantId));
  if (!hasProtection) return { plan: { kind: 'none' }, response: null };

  const plan = planProtectionMutation(cart, { enabled: true, protectionVariantId });
  if (plan.kind === 'none') return { plan, response: null };

  const response = await readJson(await fetchImpl(routes.update, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      updates: plan.updates,
      sections,
      sections_url: sectionsUrl,
    }),
  }));
  return { plan, response };
}

const checkboxSelector = '[data-os-shipping-protection] .os-shipping-protection__checkbox';

export function createProtectionController({
  documentRef,
  locationRef,
  routes,
  applyChoice = applyProtectionChoice,
  renderSections,
  subscribeCartUpdates,
  reconcileChoice = reconcileExistingProtection,
}) {
  const knownVariantIds = new Set(
    Array.from(documentRef.querySelectorAll('[data-os-shipping-protection][data-variant-id]'))
      .map((widget) => Number(widget.dataset?.variantId))
      .filter(Number.isFinite),
  );
  let isReconciling = false;

  async function handleChange(event) {
    const target = event.target;
    if (!target?.matches?.(checkboxSelector)) return;

    const widget = target.closest('[data-os-shipping-protection]');
    const protectionVariantId = Number(widget?.dataset?.variantId);
    if (!Number.isFinite(protectionVariantId)) return;
    knownVariantIds.add(protectionVariantId);

    const controls = Array.from(documentRef.querySelectorAll(checkboxSelector));
    const previousStates = controls.map((control) => control.checked);
    const status = widget.querySelector('.os-shipping-protection__status');
    controls.forEach((control) => { control.disabled = true; });
    if (status) status.textContent = 'Updating shipping protection…';

    try {
      const result = await applyChoice({
        enabled: target.checked,
        protectionVariantId,
        routes,
        sections: ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'],
        sectionsUrl: locationRef.pathname,
      });
      controls.forEach((control) => { control.checked = target.checked; });
      if (result.response) renderSections(result.response);
      if (status) status.textContent = '';
      return result;
    } catch (error) {
      controls.forEach((control, index) => { control.checked = previousStates[index]; });
      if (status) status.textContent = error.message || 'Unable to update shipping protection.';
      return null;
    } finally {
      controls.forEach((control) => { control.disabled = false; });
    }
  }

  async function handleCartUpdate() {
    if (isReconciling || knownVariantIds.size === 0) return;
    isReconciling = true;
    try {
      for (const protectionVariantId of knownVariantIds) {
        const result = await reconcileChoice({
          protectionVariantId,
          routes,
          sections: ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'],
          sectionsUrl: locationRef.pathname,
        });
        if (result.response) renderSections(result.response);
      }
    } finally {
      isReconciling = false;
    }
  }

  documentRef.addEventListener('change', handleChange);
  if (subscribeCartUpdates) subscribeCartUpdates(handleCartUpdate);
  return { handleChange, handleCartUpdate, knownVariantIds };
}

export function renderCartSections(response, { documentRef, locationRef }) {
  const normalizedPath = locationRef.pathname.replace(/\/$/, '') || '/';
  if (normalizedPath === '/cart') {
    locationRef.reload();
    return;
  }

  const cartDrawer = documentRef.querySelector('cart-drawer');
  if (cartDrawer?.renderContents && response?.sections) {
    cartDrawer.renderContents(response);
    return;
  }

  locationRef.reload();
}

function jsonRoute(route) {
  return route.endsWith('.js') ? route : `${route}.js`;
}

function subscribeToShrineCartUpdates(handler) {
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate) {
    return subscribe(PUB_SUB_EVENTS.cartUpdate, handler);
  }
  return undefined;
}

export function initializeShippingProtection({ documentRef = document, windowRef = window } = {}) {
  if (windowRef.__vollttiShippingProtectionController) {
    return windowRef.__vollttiShippingProtectionController;
  }

  const routes = {
    cart: jsonRoute(windowRef.routes.cart_url),
    add: jsonRoute(windowRef.routes.cart_add_url),
    update: jsonRoute(windowRef.routes.cart_update_url),
  };
  const controller = createProtectionController({
    documentRef,
    locationRef: windowRef.location,
    routes,
    subscribeCartUpdates: subscribeToShrineCartUpdates,
    renderSections: (response) => renderCartSections(response, {
      documentRef,
      locationRef: windowRef.location,
    }),
  });
  controller.routes = routes;
  windowRef.__vollttiShippingProtectionController = controller;
  return controller;
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  initializeShippingProtection();
}
